/* eslint-disable react-hooks/exhaustive-deps */
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import DressingRoomContext, {
  DressingRoomContextValue,
} from "contexts/DressingRoomContext";
import { immutableReplaceAt } from "utils/arrayUtils";

export type DressingRoomProviderProps = {
  tokenId: number;
  availableTraits?: number[];
  initialTraits?: number[];
};

const DressingRoomProvider = ({
  tokenId,
  initialTraits = [],
  availableTraits = [],
  children,
}: PropsWithChildren<DressingRoomProviderProps>) => {
  const [state, setState] = useState({
    history: [initialTraits],
    pointer: 0,
  });

  const currentTraits: number[] = useMemo(
    () => state.history[state.pointer],
    [state]
  );

  const newTraits = useMemo(() => {
    if (currentTraits && initialTraits) {
      const changedTraits = currentTraits.reduce(
        (prev: number[], tokenId: number) => {
          if (!initialTraits.includes(tokenId)) {
            return [...prev, tokenId];
          }
          return prev;
        },
        []
      );

      return changedTraits;
    }

    return [];
  }, [currentTraits]);

  const undoSwap = useCallback(() => {
    if (state.pointer > 0) {
      setState({ ...state, pointer: state.pointer - 1 });
    }
  }, [state]);

  const redoSwap = useCallback(() => {
    if (state.pointer + 1 < state.history.length) {
      setState({ ...state, pointer: state.pointer + 1 });
    }
  }, [state]);

  const resetTraits = useCallback(() => {
    setState({ history: [initialTraits], pointer: 0 });
  }, [initialTraits]);

  const swapTrait = useCallback(
    (newTraits: number[]) => {
      const newSwaps = [
        ...state.history.slice(0, state.pointer + 1),
        newTraits,
      ];
      setState({ history: newSwaps, pointer: state.pointer + 1 });
    },
    [state]
  );

  const removeTrait = useCallback(
    (traitId: number) => {
      const traitIndex = currentTraits.indexOf(traitId);
      if (traitIndex > -1) {
        swapTrait(
          immutableReplaceAt(
            currentTraits,
            traitIndex,
            initialTraits[traitIndex]
          )
        );
      }
    },
    [currentTraits, initialTraits, swapTrait]
  );

  useEffect(() => {
    setState({
      history: [initialTraits],
      pointer: 0,
    });
  }, [initialTraits]);

  const contextValue = useMemo<DressingRoomContextValue>(
    () => ({
      tokenId,
      color: initialTraits[1],
      currentTraits,
      newTraits,
      initialTraits,
      availableTraits,
      canRedo: state.pointer < state.history.length - 1,
      canUndo: state.pointer > 0,
      undoSwap,
      redoSwap,
      resetTraits,
      swapTrait,
      removeTrait,
    }),
    [
      state,
      tokenId,
      undoSwap,
      redoSwap,
      resetTraits,
      swapTrait,
      initialTraits,
      availableTraits,
      newTraits,
      currentTraits,
      removeTrait,
    ]
  );

  return (
    <DressingRoomContext.Provider value={contextValue}>
      {children}
    </DressingRoomContext.Provider>
  );
};

export default DressingRoomProvider;
